import { useEffect } from 'react'

import { ActionCenterTaskType } from '@domain'
import { Box, Button, Drawer, Stack, Typography } from '@mui/material'
import { DirectDealStatus } from '@services/directDeals.types'
import { StringParam, useQueryParam } from 'use-query-params'

import DirectDeal from './DirectDeal/DirectDeal'
import DirectDealsList from './DirectDealsList/DirectDealsList'

type Props = {
	onClose: () => void
}

export default function DirectDeals({ onClose }: Props) {
	const [activeTask, setActiveTask] = useQueryParam('active-task', StringParam)
	const [statusFilter, setStatusFilter] = useQueryParam('direct-deals', StringParam)
	const [selectedId, setSelectedId] = useQueryParam('direct-deal', StringParam)

	const hasSelectedDeal = !!selectedId
	const directDealsCompleted = statusFilter === DirectDealStatus.PENDING && !hasSelectedDeal

	useEffect(() => {
		if (activeTask !== ActionCenterTaskType.DIRECT_DEALS) return
		if (statusFilter === undefined || statusFilter === null) {
			setStatusFilter(DirectDealStatus.PENDING, 'replaceIn')
		}
	}, [statusFilter, setStatusFilter, activeTask])

	const handleUpdateDirectDeal = (id: Id = null) => {
		if (id) {
			setSelectedId(id.toString(), 'replaceIn')
			return
		}

		setSelectedId(null)
	}

	const handleSelect = (id: Id) => {
		if (!id) setSelectedId(null, 'replaceIn')
		setSelectedId(id.toString(), 'replaceIn')
	}

	const handleClose = () => {
		setStatusFilter(undefined)
		setActiveTask(undefined)
		setSelectedId(undefined)
		onClose()
	}

	// Only render when the drawer is open
	if (activeTask !== ActionCenterTaskType.DIRECT_DEALS) return null

	return (
		<Drawer
			open={activeTask === ActionCenterTaskType.DIRECT_DEALS}
			variant="temporary"
			anchor="right"
			onClose={handleClose}
			data-testid="direct-deals-drawer"
			sx={{ '& .MuiDrawer-paper': { width: '95vw' }, zIndex: (theme) => theme.zIndex.modal + 1 }}
		>
			<Stack direction="row" height="100%" spacing={2}>
				<DirectDealsList
					onSelect={handleSelect}
					onChangeStatus={(status) => setStatusFilter(status)}
					onClose={handleClose}
				/>
				{hasSelectedDeal && (
					<DirectDeal
						directDealId={selectedId}
						onUpdateDirectDeal={handleUpdateDirectDeal}
						// FIXME (rl) hiding the match change until the grouping is fixed
						// onMatchChange={invalidateDirectDealQueries}
					/>
				)}

				{directDealsCompleted && <DirectDealsCompleted onClose={handleClose} />}
			</Stack>
		</Drawer>
	)
}

function DirectDealsCompleted({ onClose }: { onClose: () => void }) {
	return (
		<Stack p={2} width="100%" data-testid="direct-deals-completed">
			<Typography variant="h6">Selected Manufacturer</Typography>
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Stack alignItems="center" spacing={2}>
					<Typography variant="body1" color="text.secondary">
						All Direct Deal Switches have been completed! Great work!
					</Typography>
					<Button
						variant="outlined"
						color="secondary"
						onClick={onClose}
						data-testid="direct-deals-close-button"
					>
						Close
					</Button>
				</Stack>
			</Box>
		</Stack>
	)
}
